import {
  PERMISSION_LABELS,
  type CreateRoleRequest,
  type ManagedRole,
  type RoleMembers,
  type UpdateRoleRequest,
} from '@gb-ticket/shared-types';
import { CopyIcon, Loader2Icon, PencilIcon, PlusIcon, Trash2Icon } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { usePageTitle } from '@/hooks/use-page-title';
import { ApiRequestError } from '@/lib/api';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import { adminTableFrameClassName } from '@/platform/features/admin-panel';
import { RoleFormDialog, type RoleFormMode } from '@/platform/features/roles/role-form-dialog';
import {
  fetchRoleMembers,
  roleMembersFromInUse,
  useCreateRole,
  useDeleteRole,
  useRoles,
  useUpdateRole,
} from '@/platform/features/roles/use-roles';
import { USERS_ADMIN_PATH, userAdminPath } from '@/platform/lib/admin';

type DeletePrompt =
  { kind: 'blocked'; members: RoleMembers } | { kind: 'confirm'; role: ManagedRole };

export function RolesPage() {
  usePageTitle('Berechtigungsgruppen');
  const roles = useRoles();
  const createRole = useCreateRole();
  const updateRole = useUpdateRole();
  const deleteRole = useDeleteRole();
  const [dialog, setDialog] = useState<{ mode: RoleFormMode; role: ManagedRole | null } | null>(
    null,
  );
  const [deletePrompt, setDeletePrompt] = useState<DeletePrompt | null>(null);
  const [checkingId, setCheckingId] = useState<string | null>(null);
  const busy = createRole.isPending || updateRole.isPending;

  async function save(body: CreateRoleRequest | UpdateRoleRequest) {
    try {
      if (dialog?.mode === 'edit' && dialog.role) {
        await updateRole.mutateAsync({ id: dialog.role.id, body });
        toast.success('Berechtigungsgruppe gespeichert.');
      } else if (dialog) {
        await createRole.mutateAsync(body as CreateRoleRequest);
        toast.success(
          dialog.mode === 'copy' ? 'Berechtigungsgruppe kopiert.' : 'Berechtigungsgruppe angelegt.',
        );
      }
      setDialog(null);
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Die Berechtigungsgruppe konnte nicht gespeichert werden.',
      );
    }
  }

  async function inspectDelete(role: ManagedRole) {
    setCheckingId(role.id);
    try {
      const members = await fetchRoleMembers(role.id);
      setDeletePrompt(members.count > 0 ? { kind: 'blocked', members } : { kind: 'confirm', role });
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Die Zuordnungen konnten nicht geladen werden.',
      );
    } finally {
      setCheckingId(null);
    }
  }

  async function confirmDelete(role: ManagedRole) {
    try {
      await deleteRole.mutateAsync(role.id);
      toast.success('Berechtigungsgruppe gelöscht.');
      setDeletePrompt(null);
    } catch (error) {
      if (error instanceof ApiRequestError && error.error.code === 'ROLE_IN_USE') {
        toast.error(error.message);
        const members = roleMembersFromInUse(role, error.error.details);
        if (members && members.count > 0) {
          setDeletePrompt({ kind: 'blocked', members });
        }
        return;
      }
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Die Berechtigungsgruppe konnte nicht gelöscht werden.',
      );
    }
  }

  const blocked = deletePrompt?.kind === 'blocked' ? deletePrompt.members : null;
  const confirm = deletePrompt?.kind === 'confirm' ? deletePrompt.role : null;

  return (
    <section className="space-y-6">
      <PlatformPageHeader
        title="Berechtigungsgruppen"
        description="Gruppen bündeln Rechte und können neuen Benutzerkonten automatisch zugeordnet werden."
        action={
          <Button type="button" onClick={() => setDialog({ mode: 'create', role: null })}>
            <PlusIcon className="size-4" aria-hidden />
            Gruppe hinzufügen
          </Button>
        }
      />

      {roles.isPending ? (
        <p className="flex items-center gap-2 text-muted-foreground">
          <Loader2Icon className="size-4 animate-spin" aria-hidden />
          Berechtigungsgruppen werden geladen …
        </p>
      ) : null}

      {roles.isError ? (
        <p role="alert" className="text-sm text-destructive">
          {roles.error instanceof ApiRequestError
            ? roles.error.message
            : 'Die Berechtigungsgruppen konnten nicht geladen werden.'}
        </p>
      ) : null}

      {roles.data ? (
        <div className={adminTableFrameClassName}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Typ</TableHead>
                <TableHead>Neue Benutzer</TableHead>
                <TableHead>Berechtigungen</TableHead>
                <TableHead className="text-right">Personen</TableHead>
                <TableHead className="w-32">
                  <span className="sr-only">Aktionen</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {roles.data.items.map((role) => (
                <TableRow key={role.id}>
                  <TableCell>
                    <div className="font-medium">{role.name}</div>
                    <div className="font-mono text-xs text-muted-foreground">{role.code}</div>
                    {role.description ? (
                      <div className="mt-1 text-xs text-muted-foreground">{role.description}</div>
                    ) : null}
                  </TableCell>
                  <TableCell>
                    <Badge variant={role.isSystem ? 'outline' : 'secondary'}>
                      {role.isSystem ? 'Systemrolle' : 'Eigene Gruppe'}
                    </Badge>
                  </TableCell>
                  <TableCell>{role.assignToNewUsers ? 'Ja' : 'Nein'}</TableCell>
                  <TableCell>
                    <span
                      title={role.permissions
                        .map((permission) => PERMISSION_LABELS[permission])
                        .join(', ')}
                    >
                      {role.permissions.length === 0
                        ? 'Keine Katalogrechte'
                        : `${role.permissions.length} Rechte`}
                    </span>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{role.memberCount}</TableCell>
                  <TableCell>
                    <div className="flex justify-end gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        aria-label={`${role.name} kopieren`}
                        onClick={() => setDialog({ mode: 'copy', role })}
                      >
                        <CopyIcon className="size-4" aria-hidden />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        aria-label={`${role.name} bearbeiten`}
                        onClick={() => setDialog({ mode: 'edit', role })}
                      >
                        <PencilIcon className="size-4" aria-hidden />
                      </Button>
                      {role.isSystem ? null : (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-sm"
                          aria-label={`${role.name} löschen`}
                          disabled={checkingId === role.id}
                          onClick={() => void inspectDelete(role)}
                        >
                          {checkingId === role.id ? (
                            <Loader2Icon className="size-4 animate-spin" aria-hidden />
                          ) : (
                            <Trash2Icon className="size-4" aria-hidden />
                          )}
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : null}

      <RoleFormDialog
        open={dialog !== null}
        mode={dialog?.mode ?? 'create'}
        source={dialog?.role ?? null}
        busy={busy}
        onOpenChange={(open) => {
          if (!open) {
            setDialog(null);
          }
        }}
        onSubmit={save}
      />

      <Dialog
        open={blocked !== null}
        onOpenChange={(open) => {
          if (!open) {
            setDeletePrompt((current) => (current?.kind === 'blocked' ? null : current));
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Gruppe ist noch zugeordnet</DialogTitle>
            <DialogDescription>
              {blocked ? assignedSentence(blocked.count) : null}
            </DialogDescription>
          </DialogHeader>
          {blocked ? (
            <>
              <ul className="max-h-64 space-y-2 overflow-y-auto">
                {blocked.items.map((member) => (
                  <li key={member.id} className="text-sm">
                    <Link
                      to={userAdminPath(member.id)}
                      className="font-medium underline-offset-4 hover:underline"
                    >
                      {member.displayName}
                    </Link>
                    <div className="text-muted-foreground">{member.email}</div>
                    {member.isActive ? null : (
                      <Badge variant="destructive" className="mt-1">
                        Deaktiviert
                      </Badge>
                    )}
                  </li>
                ))}
              </ul>
              {blocked.count > blocked.items.length ? (
                <p className="text-sm text-muted-foreground">
                  und {blocked.count - blocked.items.length} weitere
                </p>
              ) : null}
              <Link
                to={`${USERS_ADMIN_PATH}?role=${blocked.code}`}
                className="text-sm underline-offset-4 hover:underline"
              >
                Benutzerliste mit dieser Gruppe
              </Link>
            </>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDeletePrompt(null)}>
              Schließen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={confirm !== null}
        onOpenChange={(open) => {
          if (!open) {
            setDeletePrompt((current) => (current?.kind === 'confirm' ? null : current));
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Berechtigungsgruppe löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirm
                ? `${confirm.name} (${confirm.code}). Channel-Zuordnungen und KI-Agenten, die nur diese Gruppe nennen, verlieren sie. Freigaben und SLA behalten den Schritt, der Rollenzeiger wird leer.${
                    confirm.assignToNewUsers ? ' Neue Konten bekommen diese Gruppe nicht mehr.' : ''
                  }`
                : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteRole.isPending}>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              disabled={deleteRole.isPending}
              onClick={(event) => {
                event.preventDefault();
                if (confirm) {
                  void confirmDelete(confirm);
                }
              }}
            >
              {deleteRole.isPending ? (
                <Loader2Icon className="size-4 animate-spin" aria-hidden />
              ) : null}
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}

function assignedSentence(count: number): string {
  const people = count === 1 ? '1 Person' : `${count} Personen`;
  return `Diese Berechtigungsgruppe ist ${people} zugeordnet.`;
}
